import * as ko from 'knockout';

import { ListRequestParams } from '../api/request';
import { BaseLoadingScreen } from './base_loading_screen';
import { ListLoaderDelegate, ListLoader } from '../components/list_loader';
import { countriesApi, CountryData } from '../api/countries';
import { Country } from '../models/country';
import { session } from '../session';
import { canCreateCountry } from '../permissions';
import { Action } from '../components/basic_widgets';
import { addChangelogAction, addChangelogListAction } from './changelog';
import { ListHeaderAction } from '../components/list_header';
import { FilterDelegate } from '../components/list_filters';
import * as namesApi from '../api/names';
import { NameI18nData } from '../api/names';
import { deflateList } from '../api/serialization';
import { asArray, updateLocationWithQueryString } from '../utils';
import i18n from '../i18n';

let template = require('raw-loader!../../templates/countries.html').default;

interface CountriesFilters {
  name_prefix?: string;
  global_region_ids?: string | string[];
}

class CountriesScreen
  extends BaseLoadingScreen
  implements ListLoaderDelegate<CountryData, Country>
{
  loader: ListLoader<CountryData, Country>;
  showRegions = session.isS2bim();
  globalRegionsEnabled = !!session.tenant()?.global_regions_enabled;
  canCreate = canCreateCountry();
  listActions = ko.observableArray<ListHeaderAction>([]);

  searchPlaceholder = i18n.t('Search countries by name')();
  search = ko.observable('').extend({ throttle: 300 });

  private globalRegionFilter = ko.observableArray<NameI18nData>(null);
  newFilters: FilterDelegate[] = this.globalRegionsEnabled
    ? [
        {
          title: i18n.t('Global region')(),
          entities: this.globalRegionFilter,
          list: (params: ListRequestParams) => namesApi.list('global_regions', params),
        },
      ]
    : [];

  private subscriptions: KnockoutSubscription[] = [];

  constructor(params: { filters: CountriesFilters }) {
    super();

    if (params.filters.name_prefix) {
      this.search(params.filters.name_prefix);
    }

    let globalRegionIds = asArray(params.filters.global_region_ids);
    let globalRegionsPromise =
      globalRegionIds.length > 0 && this.globalRegionsEnabled
        ? namesApi.list('global_regions', { ids: globalRegionIds })
        : Promise.resolve<NameI18nData[]>([]);

    let promise = globalRegionsPromise.then((globalRegions) => {
      if (globalRegions) {
        this.globalRegionFilter(globalRegions);
      }
    });
    this.loadedAfter(promise);

    if (this.canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/countries/new/',
        tooltipTitle: i18n.t('Add country')(),
      });
    }
    this.listActions.push(...addChangelogListAction('country'));
  }

  onReady(loader: ListLoader<CountryData, Country>) {
    this.loader = loader;
    this.subscriptions.push(this.search.subscribe(() => this.loader.forceLoad()));
  }

  fetch(params: ListRequestParams) {
    let filters = this.getFilters();
    updateLocationWithQueryString(filters);
    return countriesApi.list({ ...filters, ...params });
  }

  private getFilters() {
    return {
      name_prefix: this.search(),
      global_region_ids: deflateList(this.globalRegionFilter),
    };
  }

  instantiate(data: CountryData) {
    return new Country(data);
  }

  getEditUrl(entity: Country) {
    return entity.editUrl();
  }

  remove(id: string) {
    return countriesApi.remove(id);
  }

  canRemove(entity: Country) {
    return entity.canEdit();
  }

  getActions(entity: Country): Action[] {
    return addChangelogAction('country', entity.id());
  }

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }
}

export let countries = {
  name: 'countries',
  viewModel: CountriesScreen,
  template: template,
};

ko.components.register(countries.name, countries);
